import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Calendar,
  CalendarClock,
  Check,
  Clock,
  LogIn,
  LogOut,
  Pencil,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react'
import { Button } from '../../../../components/Button'
import { CustomTimePicker as TimePicker } from '../../../../components/CustomTimePicker'
import type {
  StaffAttendanceRecord,
  StaffAttendanceStatus,
} from '../types/hrmsAttendanceRecords.types'
import type {
  BackendAttendanceStatus,
  EditAttendancePayload,
} from '../services/attendanceService'
import {
  computeAttendanceStatus,
  timeStringToMinutes,
} from '../utils/manualAttendanceStatus.utils'
import { useAttendanceStatusConfig } from '../hooks/useAttendanceStatusConfig'

export interface EditAttendanceModalProps {
  isOpen: boolean
  record: StaffAttendanceRecord | null
  selectedDate?: string
  isSaving?: boolean
  onClose: () => void
  onSave: (
    recordId: string,
    updates: EditAttendancePayload
  ) => void | Promise<void>
}

function mapToBackendStatus(status: StaffAttendanceStatus): BackendAttendanceStatus {
  switch (status) {
    case 'Present':
      return 'PRESENT'
    case 'Late':
      return 'LATE'
    case 'Absent':
      return 'ABSENT'
    case 'Half Day':
      return 'HALF_DAY'
    case 'On Leave':
      return 'ON_LEAVE'
    default:
      return 'PRESENT'
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatShortDate(dateStr?: string): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatFullDate(dateStr?: string): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function to12h(timeStr?: string): string {
  if (!timeStr || timeStr === 'none' || timeStr === '—') return '—'
  if (/am|pm/i.test(timeStr)) return timeStr.trim().toUpperCase()
  const [hStr, mStr = '00'] = timeStr.trim().split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return timeStr
  const period = h >= 12 ? 'PM' : 'AM'
  const displayH = h % 12 === 0 ? 12 : h % 12
  return `${String(displayH).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`
}

function to24h(timeStr?: string): string {
  if (!timeStr || timeStr === 'none') return ''
  if (!/am|pm/i.test(timeStr)) {
    const [h = '00', m = '00'] = timeStr.trim().split(':')
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`
  }
  const isPm = /pm/i.test(timeStr)
  const isAm = /am/i.test(timeStr)
  const clean = timeStr.replace(/am|pm|\s/gi, '')
  const [hStr = '0', mStr = '0'] = clean.split(':')
  let h = parseInt(hStr, 10) || 0
  const m = parseInt(mStr, 10) || 0
  if (isPm && h < 12) h += 12
  if (isAm && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function getStatusDotColor(status: StaffAttendanceStatus): string {
  switch (status) {
    case 'Present':
      return 'bg-emerald-500'
    case 'Late':
      return 'bg-amber-500'
    case 'Half Day':
      return 'bg-sky-500'
    case 'Absent':
      return 'bg-red-500'
    case 'On Leave':
      return 'bg-slate-400'
    default:
      return 'bg-slate-400'
  }
}

export function EditAttendanceModal({
  isOpen,
  record,
  selectedDate,
  isSaving = false,
  onClose,
  onSave,
}: EditAttendanceModalProps) {
  const [checkIn, setCheckIn] = useState<string>('')
  const [checkOut, setCheckOut] = useState<string>('')
  const [originalCheckIn, setOriginalCheckIn] = useState<string>('')
  const [originalCheckOut, setOriginalCheckOut] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [reasonTouched, setReasonTouched] = useState<boolean>(false)

  // Reset form whenever modal opens with a different record
  useEffect(() => {
    if (isOpen && record) {
      const initIn = record.checkIn || '09:00'
      const initOut = record.checkOut || '17:00'
      setCheckIn(initIn)
      setCheckOut(initOut)
      setOriginalCheckIn(initIn)
      setOriginalCheckOut(initOut)
      setReason('')
      setReasonTouched(false)
    }
  }, [isOpen, record])

  // ESC key listener
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [isOpen, onClose])

  // Body overflow lock
  useEffect(() => {
    if (!isOpen) return
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = orig
    }
  }, [isOpen])

  const { config: statusConfig } = useAttendanceStatusConfig()

  if (!isOpen || !record) return null

  const attendanceDate = record.date || selectedDate || '2026-09-24'
  const isBiometric = Boolean(
    record.source && record.source.toLowerCase().includes('biometric')
  )
  const currentSourceLabel = record.source || 'Biometric Sync'

  // Time calculations
  const checkIn24 = to24h(checkIn)
  const checkOut24 = to24h(checkOut)
  const inMin = timeStringToMinutes(checkIn24)
  const outMin = timeStringToMinutes(checkOut24)

  const hasTimes = inMin !== null && outMin !== null
  const isValidRange = hasTimes && outMin > inMin

  const workingMinutes = hasTimes && isValidRange ? outMin - inMin : 0
  const calcHours = Math.floor(workingMinutes / 60)
  const calcMins = workingMinutes % 60
  const calculatedHoursText = `${calcHours}h ${calcMins}m`

  // Original working hours
  const origInMin = timeStringToMinutes(to24h(originalCheckIn))
  const origOutMin = timeStringToMinutes(to24h(originalCheckOut))
  const origWorkingMinutes =
    origInMin !== null && origOutMin !== null && origOutMin > origInMin
      ? origOutMin - origInMin
      : record.workingMinutes ?? 0
  const origHours = Math.floor(origWorkingMinutes / 60)
  const origMins = origWorkingMinutes % 60
  const origHoursText = `${origHours}h ${origMins}m`

  // Modification checks
  const isCheckInModified = to24h(checkIn) !== to24h(originalCheckIn)
  const isCheckOutModified = to24h(checkOut) !== to24h(originalCheckOut)
  const isTimesModified = isCheckInModified || isCheckOutModified
  const modifiedCount =
    (isCheckInModified ? 1 : 0) + (isCheckOutModified ? 1 : 0)

  // Pure function status computation
  const computedStatusRaw = statusConfig
    ? computeAttendanceStatus(checkIn24, checkOut24, statusConfig)
    : null

  let displayStatus: StaffAttendanceStatus | '-' = '-'
  if (computedStatusRaw === 'LATE') displayStatus = 'Late'
  else if (computedStatusRaw === 'HALF_DAY') displayStatus = 'Half Day'
  else if (computedStatusRaw === 'ABSENT') displayStatus = 'Absent'
  else if (computedStatusRaw === 'PRESENT') displayStatus = 'Present'
  else if (!statusConfig) displayStatus = '-'

  const isCheckOutAfterCheckIn = Boolean(hasTimes && isValidRange)
  const isReasonValid = reason.trim().length > 0
  const isSaveEnabled = isTimesModified && isCheckOutAfterCheckIn && isReasonValid

  const handleSave = () => {
    if (!isSaveEnabled || isSaving) return

    const updates: EditAttendancePayload = {}
    if (isCheckInModified) {
      updates.checkInTime = to24h(checkIn)
    }
    if (isCheckOutModified) {
      updates.checkOutTime = to24h(checkOut)
    }
    if (displayStatus !== '-') {
      const backendStatus = mapToBackendStatus(displayStatus)
      const origStatus = mapToBackendStatus(record.status)
      if (backendStatus !== origStatus) {
        updates.status = backendStatus
      }
    }
    if (reason.trim()) {
      updates.remarks = reason.trim()
    }

    onSave(record.id, updates)
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-attendance-modal-title"
      className="fixed inset-0 z-[150] flex items-center justify-center p-3 sm:p-4 overflow-x-hidden font-sans"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Modal Box */}
      <div
        className="relative z-10 w-full max-w-[560px] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Header */}
        <div className="bg-gray-50 border-b border-slate-200 px-5 py-4 flex items-start justify-between shrink-0 gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#146971]/10 text-[#146971] border border-[#146971]/20 shrink-0 mt-0.5">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2
                id="edit-attendance-modal-title"
                className="text-base sm:text-lg font-bold text-slate-900 truncate"
              >
                Edit Attendance
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 leading-normal">
                Update the employee's attendance time for the selected date.
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close dialog"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {/* 2. Employee Card */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#146971] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {getInitials(record.name)}
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-slate-900 truncate">
                    {record.name}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">
                    {record.designation || 'Faculty'} ·{' '}
                    {record.department || 'Department'}
                  </p>
                </div>
              </div>
              <span className="bg-slate-100 text-slate-700 font-semibold px-2.5 py-0.5 rounded text-xs border border-slate-200 shrink-0">
                {record.employeeId}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/70">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                  ATTENDANCE DATE
                </span>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                  <Calendar className="h-3.5 w-3.5 text-slate-500" />
                  <span>{formatShortDate(attendanceDate)}</span>
                </div>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                  CURRENT SOURCE
                </span>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-600 shrink-0" />
                  <span>{currentSourceLabel}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Section Heading Row */}
          <div className="flex items-center justify-between text-xs pt-1">
            <div className="flex items-center gap-1.5 font-bold uppercase text-[#146971] tracking-wider">
              <Clock className="h-3.5 w-3.5" />
              <span>ATTENDANCE INFORMATION</span>
            </div>
            {statusConfig ? (
              <span className="text-[11px] font-medium text-slate-500">
                Institution Rule: {statusConfig.dailyWorkingHours}h = Full Day
              </span>
            ) : (
              <span className="text-[11px] font-medium text-slate-400">-</span>
            )}
          </div>

          {/* 4. Attendance Date Read-Only Input */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <label className="font-semibold text-slate-700">
                ATTENDANCE DATE
              </label>
              <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                READ-ONLY
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-xs text-slate-700 cursor-not-allowed">
              <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="font-medium">
                {formatFullDate(attendanceDate)}
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              The attendance date cannot be modified for this existing log record.
            </p>
          </div>

          {/* 5. Two Columns: Check-in & Check-out */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700 block">
                CHECK-IN TIME <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <LogIn className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none z-10" />
                <div className="w-full [&>div>button]:pl-9">
                  <TimePicker
                    value={checkIn}
                    onChange={(val) => setCheckIn(val)}
                    placeholder="09:00 AM"
                    className="w-full"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Original: {to12h(originalCheckIn)}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700 block">
                CHECK-OUT TIME <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <LogOut className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none z-10" />
                <div className="w-full [&>div>button]:pl-9">
                  <TimePicker
                    value={checkOut}
                    onChange={(val) => setCheckOut(val)}
                    placeholder="05:00 PM"
                    className="w-full"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Original: {to12h(originalCheckOut)}
              </p>
              {!isCheckOutAfterCheckIn && hasTimes && (
                <p className="text-[11px] font-semibold text-red-500">
                  Check-out time must be after check-in time
                </p>
              )}
            </div>
          </div>

          {/* 6. Calculated Working Hours & Status Card */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 grid grid-cols-2 gap-4">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                CALCULATED WORKING HOURS
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-base font-bold text-slate-900">
                  {isValidRange ? calculatedHoursText : '—'}
                </span>
                {isTimesModified && isValidRange && (
                  <span className="line-through text-xs text-slate-400 font-normal">
                    {origHoursText}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-semibold text-[#146971] block mt-0.5">
                Auto-calculated
              </span>
            </div>

            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                ATTENDANCE STATUS
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                {displayStatus !== '-' && (
                  <span
                    className={`h-2 w-2 rounded-full ${getStatusDotColor(
                      displayStatus
                    )}`}
                  />
                )}
                <span className="text-sm font-bold text-slate-900">
                  {displayStatus}
                </span>
              </div>
              {statusConfig ? (
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  Rule: &gt;= {statusConfig.dailyWorkingHours}h Full Day
                </span>
              ) : (
                <span className="text-[11px] text-slate-400 block mt-0.5">-</span>
              )}
            </div>
          </div>

          {/* 7. Section Heading Row: REASON FOR CHANGE */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 font-bold uppercase text-slate-800 tracking-wider">
                <Pencil className="h-3.5 w-3.5 text-slate-500" />
                <span>REASON FOR CHANGE</span>
              </div>
              <span className="text-[10px] font-bold uppercase text-red-600 tracking-wider">
                REQUIRED FOR AUDIT
              </span>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <label className="font-semibold text-slate-700">
                  REASON FOR EDITING <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] font-bold text-slate-400 uppercase">
                  {reason.length} CHARS
                </span>
              </div>
              <textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setReasonTouched(true)}
                placeholder="Provide a detailed reason for modifying this attendance record..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-900 focus:bg-white focus:border-[#146971] focus:outline-none focus:ring-1 focus:ring-[#146971] resize-none"
              />
              {reasonTouched && !reason.trim() && (
                <p className="text-[11px] font-semibold text-red-500">
                  Reason for editing is required
                </p>
              )}
              <p className="text-[11px] text-slate-400 leading-normal">
                This justification is permanently bound to the immutable HRMS
                compliance ledger alongside the editor's signature.
              </p>
            </div>
          </div>

          {/* 8. Info Card for Biometric Source Only */}
          {isBiometric && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5 flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
              <div className="text-xs">
                <h4 className="font-bold text-slate-900">
                  Biometric Log Synchronization Policy
                </h4>
                <p className="text-slate-500 mt-0.5 leading-relaxed">
                  This attendance record was synced from the biometric system.
                  Manual changes will be recorded in the attendance audit
                  history.
                </p>
              </div>
            </div>
          )}

          {/* 9. Change Summary Card */}
          {isTimesModified && isValidRange && (
            <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-3.5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-bold text-[#146971] uppercase tracking-wider">
                  <Sparkles className="h-4 w-4" />
                  <span>CHANGE SUMMARY</span>
                </div>
                <span className="rounded-full bg-teal-100 text-[#146971] font-bold text-[10px] px-2.5 py-0.5 uppercase tracking-wide">
                  {modifiedCount} FIELD(S) MODIFIED
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-white border border-cyan-100 p-2.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                    CHECK-IN TIME
                  </span>
                  {isCheckInModified ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="line-through text-red-500 font-medium">
                        {to12h(originalCheckIn)}
                      </span>
                      <span className="text-slate-400">→</span>
                      <span className="font-bold text-slate-900">
                        {to12h(checkIn)}
                      </span>
                    </div>
                  ) : (
                    <div className="text-slate-600 font-medium">
                      {to12h(checkIn)}{' '}
                      <span className="text-slate-400 text-[11px] font-normal">
                        (Unchanged)
                      </span>
                    </div>
                  )}
                </div>

                <div className="rounded-lg bg-white border border-cyan-100 p-2.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                    CHECK-OUT TIME
                  </span>
                  {isCheckOutModified ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="line-through text-red-500 font-medium">
                        {to12h(originalCheckOut)}
                      </span>
                      <span className="text-slate-400">→</span>
                      <span className="font-bold text-slate-900">
                        {to12h(checkOut)}
                      </span>
                    </div>
                  ) : (
                    <div className="text-slate-600 font-medium">
                      {to12h(checkOut)}{' '}
                      <span className="text-slate-400 text-[11px] font-normal">
                        (Unchanged)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between text-xs pt-1 border-t border-cyan-100/80">
                <span className="text-slate-600 font-medium">
                  Updated Working Hours:
                </span>
                <span className="font-bold text-slate-900">
                  {calculatedHoursText} ({displayStatus})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 10. Sticky Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-slate-200 px-5 py-3.5 flex items-center justify-end gap-2.5 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 border-none rounded-lg px-4 h-9 font-medium"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!isSaveEnabled || isSaving}
            icon={<Check className="h-4 w-4" />}
            onClick={handleSave}
            className="rounded-lg px-4 h-9 font-medium bg-[#146971] hover:bg-[#0f565d]"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
